import { Building2, CalendarClock, ChevronsUpDown, CreditCard, FolderClosed, Home, LayoutGrid, Settings, Sparkles, Store, UserCog, Users, Users2 } from "lucide-react";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { cn } from "../lib/cn";
import { useOrg } from "../lib/hooks";
import { Logo } from "./Logo";

type IconCmp = ComponentType<{ size?: number | string; className?: string }>;

const NAV: { to: string; labelKey: string; icon: IconCmp; match: string[]; exact?: boolean }[] = [
  { to: "/app", labelKey: "nav.overview", icon: LayoutGrid, match: ["/app"], exact: true },
  { to: "/app/clients", labelKey: "nav.clients", icon: Users, match: ["/app/clients", "/app/deals"] },
  { to: "/app/properties", labelKey: "nav.properties", icon: Home, match: ["/app/properties"] },
  { to: "/app/sellers", labelKey: "nav.sellers", icon: Store, match: ["/app/sellers"] },
  { to: "/app/agenda", labelKey: "nav.agenda", icon: CalendarClock, match: ["/app/agenda"] },
  { to: "/app/documents", labelKey: "nav.documents", icon: FolderClosed, match: ["/app/documents"] },
  { to: "/app/team", labelKey: "nav.team", icon: Users2, match: ["/app/team"] },
  { to: "/app/settings", labelKey: "nav.settings", icon: Settings, match: ["/app/settings"] },
];

// Workspace dropdown: organization settings live under /app/settings/*; the
// personal Account page is now its own top-level route (/app/account), kept
// visually separate at the foot of the menu.
const WORKSPACE_MENU: { to: string; labelKey: string; icon: IconCmp }[] = [
  { to: "/app/settings/organization", labelKey: "settings.nav.organization", icon: Building2 },
  { to: "/app/settings/members", labelKey: "settings.nav.members", icon: Users2 },
  { to: "/app/settings/billing", labelKey: "settings.nav.billing", icon: CreditCard },
];

const ACCOUNT_MENU_ITEM: { to: string; labelKey: string; icon: IconCmp } = {
  to: "/app/account",
  labelKey: "settings.nav.account",
  icon: UserCog,
};

export function Sidebar() {
  const { data: org } = useOrg();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as globalThis.Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  return (
    <aside className="flex h-full w-60 shrink-0 flex-col border-r border-line bg-surface-1">
      <div className="px-4 py-4">
        <Link to="/app">
          <Logo />
        </Link>
      </div>

      <nav className="flex-1 space-y-0.5 px-3">
        {NAV.map(({ to, labelKey, icon: Icon, match, exact }) => {
          const active = exact
            ? pathname === to || pathname === `${to}/`
            : match.some((m) => pathname.startsWith(m));
          return (
            <Link
              key={to}
              to={to}
              className={cn(
                "group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                active
                  ? "bg-brand/10 font-semibold text-ink"
                  : "font-medium text-ink-muted hover:bg-surface-2 hover:text-ink",
              )}
            >
              <Icon
                size={17}
                className={cn(
                  "shrink-0",
                  active ? "text-brand-soft" : "text-ink-faint group-hover:text-ink-muted",
                )}
              />
              {t(labelKey)}
            </Link>
          );
        })}
      </nav>

      <TrialCard />

      <div className="relative border-t border-line p-3" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className="ring-focus flex w-full items-center gap-2.5 rounded-md px-1 py-1 text-left transition-colors hover:bg-surface-2"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-gradient-to-br from-brand to-brand-dim text-xs font-bold text-white">
            {(org?.name ?? "··").slice(0, 1)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-ink">{org?.name ?? "—"}</div>
            <div className="text-xs text-ink-faint">
              {org?.plan
                ? t("nav.planWorkspace", {
                    plan: t(`nav.plan_${org.plan}`, { defaultValue: org.plan }),
                  })
                : t("nav.workspace")}
            </div>
          </div>
          <ChevronsUpDown size={15} className="shrink-0 text-ink-faint" />
        </button>

        {menuOpen && (
          <div role="menu" className="card animate-scale-in absolute inset-x-3 bottom-full z-50 mb-2 p-1.5 shadow-pop">
            {WORKSPACE_MENU.map((m) => (
              <Link
                key={m.to}
                to={m.to}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
              >
                <m.icon size={15} /> {t(m.labelKey)}
              </Link>
            ))}
            <div className="my-1 h-px bg-line" />
            <Link
              to={ACCOUNT_MENU_ITEM.to}
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
            >
              <ACCOUNT_MENU_ITEM.icon size={15} /> {t(ACCOUNT_MENU_ITEM.labelKey)}
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}

/**
 * Free-trial countdown card (no-card trial from onboarding). Shows the days
 * left on a 7-day scale with an upgrade CTA into Billing; flips to an
 * "expired" nudge once the trial lapses (the server degrades the status to
 * `none` at read time). Hidden for paid/canceled orgs and while billing is
 * unknown (demo mode / loading / error).
 */
function TrialCard() {
  const { t } = useTranslation();
  const { data: b } = useQuery({ queryKey: ["billing"], queryFn: api.getBilling, retry: false });
  if (!b?.trialEndsAt) return null;

  const trialing = b.status === "trialing";
  const expired = b.status === "none"; // had a trial (trialEndsAt set), now lapsed
  if (!trialing && !expired) return null;

  const msLeft = new Date(b.trialEndsAt).getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / 864e5));
  const pct = Math.max(0, Math.min(1, msLeft / (7 * 864e5)));

  return (
    <div className="mx-3 mb-3 rounded-lg border border-line bg-surface-2 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-ink">
        <Sparkles size={13} className="shrink-0 text-brand-soft" />
        {trialing ? t("nav.trialTitle") : t("nav.trialExpiredTitle")}
      </div>
      <div className="mt-1 text-xs text-ink-muted">
        {trialing ? t("nav.trialDaysLeft", { count: daysLeft }) : t("nav.trialExpiredBody")}
      </div>
      {trialing && (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-surface-3">
          <div
            className="h-full rounded-full bg-brand transition-[width]"
            style={{ width: `${Math.round(pct * 100)}%` }}
          />
        </div>
      )}
      <Link
        to="/app/settings/billing"
        className="ring-focus mt-2.5 block rounded-md bg-brand px-3 py-1.5 text-center text-xs font-semibold text-white transition-colors hover:bg-brand-dim"
      >
        {t("nav.trialUpgrade")}
      </Link>
    </div>
  );
}
